import { MasterTemplate, ScaleResume } from '@sr/templates';
import { Resume as NewResumeType, Resume } from '@sr/types';
import { useState } from 'react';
import { Box, Button, Heading, Image } from 'theme-ui';
import { DownloadIcon, MinusIcon, PlusIcon } from 'components/ui/icons';
import { Link, LinkArrow, LinkButtonArrow } from 'components/ui/links';
import { ResumeExampleFieldsFragment as ResumeExample } from 'graphql/generated/sdk';
import { text } from 'styles/theme';

type Props = {
  example: ResumeExample;
  resume: Resume;
};

export default function Example({ example, resume }: Props) {
  const { title, resumeId } = example;
  const [isExpanded, setIsExpanded] = useState(false);

  // Typecast resume to @sr/types Resume that templates expect
  const castResume = resume as NewResumeType;

  return (
    <Box as="section" sx={{ mb: [90, 120] }}>
      <Box
        as="header"
        variant="layout.content"
        sx={{
          display: 'flex',
          flexDirection: ['column', 'row'],
          justifyContent: [null, 'space-between'],
          mb: [12, 24],
        }}
      >
        <Heading
          as="h2"
          variant="displaySmall"
          sx={{
            pl: [0, 24],
            mb: [8, 0],
          }}
        >
          {title}
        </Heading>
      </Box>
      <Box variant="grids.full" sx={{ justifyItems: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            bg: example.backgroundColor,
            px: [16, 48, 80],
            pt: [24, 32, 64],
            pb: [90, 128],
            borderRadius: 'large',
            overflow: 'hidden',
            maxHeight: isExpanded ? 'auto' : [350, 500, 700],
            maxWidth: 980,
            width: '100%',
            gridColumn: '1 / -1',
          }}
        >
          <Button
            variant="withIconSecondary"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              position: 'absolute',
              bottom: [12, 24],
              left: [12, 24],
              zIndex: 2,
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.15)',
            }}
          >
            {isExpanded ? (
              <MinusIcon sx={{ mr: [0, 8] }} />
            ) : (
              <PlusIcon sx={{ mr: [0, 8] }} />
            )}
            <Box sx={{ display: ['none', 'block'] }}>
              {isExpanded ? 'Show less' : 'View full resume'}
            </Box>
          </Button>
          <LinkButtonArrow
            href={getSignupLink(resume)}
            sx={{
              position: 'absolute',
              bottom: [12, 24],
              right: [12, 24],
              zIndex: 2,
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.15)',
            }}
          >
            Use this template
          </LinkButtonArrow>
          <Box
            sx={{
              filter: 'drop-shadow(16px 44px 76px rgba(0, 0, 0, 0.32))',
              mx: 'auto',
            }}
          >
            <ScaleResume>
              <MasterTemplate
                resume={castResume}
                forceDefaultBaseSize={true}
                forceBreakpoint="desktop"
              />
            </ScaleResume>
          </Box>
          {/* This isn't visible. It's here for Google to pick up and add to image search results */}
          <Image
            src={getImageUrl(resumeId)}
            alt={`${title} resume template sample made with Standard Resume`}
            loading="lazy"
            width={1200}
            height={630}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: -1,
              objectFit: 'cover',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

function getSignupLink(resume: Resume) {
  const templateId = resume.style.activeTemplateId;
  return `${process.env.NEXT_PUBLIC_APP_SETUP_URL}?templateid=${templateId}`;
}

function getImageUrl(resumeId: string) {
  const sourceUrl = `https://app.standardresume.co/assets-api/v2/preview/${resumeId}`;
  return `https://assets.standardresume.co/image/fetch/${encodeURIComponent(
    sourceUrl
  )}`;
}
